/**
 * Pays for an order through Razorpay Checkout (payment-flow.md §4.3; ORD-01 / D-03), keeping the
 * backend in step exactly as the patient app does:
 *
 *   start (or resume) attempt → Checkout ─ success → confirm  (server verifies with Razorpay)
 *                                        └ closed  → fail (after a decline) / cancel
 *
 * A `pending` outcome (the bank is still confirming, or the server couldn't reach Razorpay) is
 * polled for a short while; the webhook settles it after that. Paying again is always safe: within
 * the payment window the server hands back the same Razorpay order, and Razorpay lets an order be
 * paid only once. A dev backend without Razorpay keys runs the fake gateway (`attempt.gateway ===
 * "fake"`): there is no Checkout, so the dev simulate endpoint settles the attempt instead.
 * Razorpay test-mode keys work as-is; production keys are a backend config change.
 *
 * Never throws — the returned result says what to show.
 */

import { ApiError } from "../api/errors";
import {
  cancelPaymentAttempt,
  confirmPaymentAttempt,
  createPaymentAttempt,
  failPaymentAttempt,
  getPaymentAttempt,
  simulatePaymentCapture,
} from "../api/payments";
import type { PaymentAttempt, PaymentFailure, PaymentStatus } from "../api/payments";

// ----- Razorpay Checkout (browser SDK) -----

interface RazorpaySuccess {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}
interface RazorpayFailure {
  error?: {
    code?: string;
    description?: string;
    source?: string;
    step?: string;
    reason?: string;
    metadata?: { payment_id?: string; order_id?: string };
  };
}
interface RazorpayInstance {
  open: () => void;
  on: (event: "payment.failed", handler: (response: RazorpayFailure) => void) => void;
}
interface RazorpayConstructor {
  new (options: Record<string, unknown>): RazorpayInstance;
}

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor;
  }
}

const CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
let sdk: Promise<RazorpayConstructor> | null = null;

/** Load Razorpay's Checkout script once; rejects when it can't load (offline, a blocker). */
function loadRazorpay(): Promise<RazorpayConstructor> {
  if (window.Razorpay) return Promise.resolve(window.Razorpay);
  if (!sdk) {
    sdk = new Promise<RazorpayConstructor>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = CHECKOUT_SRC;
      script.async = true;
      script.onload = () => (window.Razorpay ? resolve(window.Razorpay) : reject(new Error("no Razorpay")));
      script.onerror = () => reject(new Error("checkout.js failed to load"));
      document.head.appendChild(script);
    }).catch((err) => {
      sdk = null; // let the next tap try again
      throw err;
    });
  }
  return sdk;
}

// ----- Result model (same as the patient app's payment.ts) -----

export type PaymentStage = "preparing" | "checkout" | "verifying";

export type PaymentResult = {
  status: "succeeded" | "pending" | "failed" | "cancelled" | "expired" | "already_paid" | "error";
  message: string;
  /** The patient can tap Pay again. */
  retryable: boolean;
  orderStatus?: string;
  paidAt?: string | null;
  attemptId?: string;
  rzpPaymentId?: string | null;
};

export type PayOptions = {
  onStage?: (stage: PaymentStage) => void;
  /** Polling stops once this returns false (the page was left). */
  isActive?: () => boolean;
};

const POLL_INTERVAL_MS = 2500;
const POLL_ROUNDS = 8; // ~20 s; the webhook takes over after that
// The brand navy (index.css --color-brand), for the Checkout sheet.
const THEME_COLOR = "#0E2B70";

export const STILL_PENDING =
  "Your bank is still confirming this payment. We'll update your order as soon as it does — there's no need to pay again.";
const OFFLINE = "You're offline. Check your internet connection and try again.";
const BUSY = "A payment is already in progress.";
const START_ERRORS: Record<string, string> = {
  ORDER_NOT_PAYABLE: "This order isn't awaiting payment any more. Refresh the page to see its status.",
  ORDER_AMOUNT_INVALID: "This order doesn't have a payable amount yet. Please contact support.",
  PAYMENT_GATEWAY_ERROR: "The payment service is unreachable right now. Please try again in a moment.",
  PRICE_BEING_REVISED:
    "The pharmacy is updating this order's price. You can pay once the new price is ready — try again in a few minutes.",
};
const NOT_RETRYABLE = new Set(["ORDER_NOT_PAYABLE", "ORDER_AMOUNT_INVALID", "UNAUTHORIZED"]);

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const isOffline = (err: unknown) => err instanceof TypeError; // fetch couldn't reach the server
const messageOf = (err: unknown, fallback: string) =>
  isOffline(err) ? OFFLINE : err instanceof ApiError ? err.message : fallback;

const fromServer = (res: PaymentStatus): PaymentResult => ({
  status: res.status,
  message: res.message,
  retryable: res.retryable,
  orderStatus: res.order_status_code,
  paidAt: res.paid_at,
  attemptId: res.attempt?.id,
  rzpPaymentId: res.attempt?.rzp_payment_id ?? null,
});

/** The order is paid (now or earlier). */
export const isPaid = (result: PaymentResult): boolean =>
  result.status === "already_paid" ||
  (result.status === "succeeded" && (!!result.paidAt || result.orderStatus === "paid"));

/** Re-ask the server (which re-checks Razorpay) while the outcome stays `pending`. */
async function pollWhilePending(
  orderId: string,
  attemptId: string,
  first: PaymentResult,
  opts: PayOptions,
): Promise<PaymentResult> {
  let result = first;
  for (let round = 0; round < POLL_ROUNDS && result.status === "pending"; round++) {
    await sleep(POLL_INTERVAL_MS);
    if (opts.isActive && !opts.isActive()) break;
    try {
      result = { ...fromServer(await getPaymentAttempt(orderId, attemptId)), rzpPaymentId: result.rzpPaymentId ?? null };
    } catch {
      // A network blip mid-poll must not turn a real payment into an error; keep polling.
    }
  }
  return result.status === "pending" ? { ...result, message: STILL_PENDING } : result;
}

const clip = (value: string | undefined, max: number) => (value ? value.slice(0, max) : null);
const toFailure = (err: NonNullable<RazorpayFailure["error"]>): PaymentFailure => ({
  code: clip(err.code, 50),
  reason: clip(err.reason, 100),
  description: clip(err.description, 500),
  source: clip(err.source, 50),
  step: clip(err.step, 50),
  rzp_payment_id: clip(err.metadata?.payment_id, 100),
});

type CheckoutOutcome =
  | { kind: "success"; response: RazorpaySuccess }
  | { kind: "closed"; lastError?: RazorpayFailure["error"] };

function openCheckout(Razorpay: RazorpayConstructor, orderId: string, attempt: PaymentAttempt): Promise<CheckoutOutcome> {
  return new Promise((resolve) => {
    let lastError: RazorpayFailure["error"];
    const prefill = attempt.prefill ?? {};
    const timeout = Number(attempt.checkout_timeout_seconds);
    const rzp = new Razorpay({
      key: attempt.rzp_key_id,
      order_id: attempt.rzp_order_id,
      amount: attempt.amount_paise,
      currency: attempt.currency || "INR",
      name: attempt.checkout_name,
      description: attempt.checkout_description,
      ...(attempt.checkout_image ? { image: attempt.checkout_image } : {}),
      prefill: {
        ...(prefill.name ? { name: prefill.name } : {}),
        ...(prefill.contact ? { contact: prefill.contact } : {}),
        ...(prefill.email ? { email: prefill.email } : {}),
      },
      notes: { order_id: orderId, ...(attempt.order_ref ? { order_ref: attempt.order_ref } : {}) },
      theme: { color: THEME_COLOR },
      // Let the patient try another method inside the sheet after a decline.
      retry: { enabled: true, max_count: 4 },
      ...(Number.isFinite(timeout) && timeout > 0 ? { timeout } : {}),
      handler: (response: RazorpaySuccess) => resolve({ kind: "success", response }),
      // Closing the sheet ends this run: after a decline it is a failure, otherwise a cancel.
      modal: { confirm_close: true, ondismiss: () => resolve({ kind: "closed", lastError }) },
    });
    rzp.on("payment.failed", (response) => {
      lastError = response.error;
    });
    rzp.open();
  });
}

let activeRun: object | null = null;

export async function payForOrder(orderId: string, opts: PayOptions = {}): Promise<PaymentResult> {
  // One Checkout at a time: a second sheet opened while one is pending would race its outcome.
  if (activeRun) return { status: "error", message: BUSY, retryable: false };
  const run = {};
  activeRun = run;
  try {
    return await runPayment(orderId, opts);
  } finally {
    if (activeRun === run) activeRun = null;
  }
}

async function runPayment(orderId: string, opts: PayOptions): Promise<PaymentResult> {
  opts.onStage?.("preparing");
  let attempt: PaymentAttempt;
  try {
    attempt = await createPaymentAttempt(orderId);
  } catch (err) {
    const code = err instanceof ApiError ? err.code : undefined;
    if (code === "ORDER_ALREADY_PAID") {
      return { status: "already_paid", message: "This order has already been paid.", retryable: false };
    }
    return {
      status: "error",
      message: code && START_ERRORS[code] ? START_ERRORS[code] : messageOf(err, "Could not start the payment. Please try again."),
      retryable: !code || !NOT_RETRYABLE.has(code),
    };
  }
  const attemptId = attempt.payment_attempt_id;

  if (attempt.gateway === "fake") {
    // Dev backend without Razorpay keys: no real Checkout exists, so simulate a capture.
    // Dev builds and the e2e build (vite --mode e2e, a fake-gateway API) may simulate.
    if (!import.meta.env.DEV && import.meta.env.MODE !== "e2e") {
      return { status: "error", message: "Payments are unavailable right now. Please try again later.", retryable: false };
    }
    opts.onStage?.("verifying");
    try {
      await simulatePaymentCapture(orderId, "captured");
      return await pollWhilePending(orderId, attemptId, fromServer(await getPaymentAttempt(orderId, attemptId)), opts);
    } catch (err) {
      return { status: "error", message: messageOf(err, "The simulated payment failed."), retryable: true, attemptId };
    }
  }

  let Razorpay: RazorpayConstructor;
  try {
    Razorpay = await loadRazorpay();
  } catch {
    // Release the attempt so it doesn't block the pharmacy's price edits for the whole window.
    await cancelPaymentAttempt(orderId, attemptId, "checkout_unavailable").catch(() => undefined);
    return {
      status: "error",
      message: "Couldn't open the payment window. Check your connection (or allow checkout.razorpay.com) and try again.",
      retryable: true,
      attemptId,
    };
  }

  opts.onStage?.("checkout");
  const outcome = await openCheckout(Razorpay, orderId, attempt);
  opts.onStage?.("verifying");

  if (outcome.kind === "success") {
    const { razorpay_payment_id, razorpay_order_id, razorpay_signature } = outcome.response;
    try {
      const res = await confirmPaymentAttempt(orderId, attemptId, {
        rzp_payment_id: razorpay_payment_id,
        rzp_order_id: razorpay_order_id,
        rzp_signature: razorpay_signature,
      });
      return await pollWhilePending(orderId, attemptId, fromServer(res), opts);
    } catch {
      // Checkout said it succeeded, so the money may well have moved: poll (the server checks
      // Razorpay itself) instead of reporting a failure.
      return pollWhilePending(
        orderId,
        attemptId,
        { status: "pending", message: STILL_PENDING, retryable: false, attemptId, rzpPaymentId: razorpay_payment_id },
        opts,
      );
    }
  }

  try {
    const res = outcome.lastError
      ? await failPaymentAttempt(orderId, attemptId, toFailure(outcome.lastError))
      : await cancelPaymentAttempt(orderId, attemptId);
    const result = fromServer(res);
    if (result.status === "pending") return await pollWhilePending(orderId, attemptId, result, opts);
    // The patient closed the sheet without a decline: show nothing alarming.
    if (!outcome.lastError && result.status === "failed") return { ...result, status: "cancelled" };
    if (result.status === "failed" && outcome.lastError?.description) {
      return { ...result, message: outcome.lastError.description };
    }
    return result;
  } catch {
    // Couldn't report it; the attempt stays open and the next Pay resumes it.
    return outcome.lastError
      ? { status: "failed", message: outcome.lastError.description || "The payment didn't go through.", retryable: true, attemptId }
      : { status: "cancelled", message: "Payment cancelled.", retryable: true, attemptId };
  }
}
